// RoomItemsTable.js - renders the line items for a single Room/Category in Section A or B.
// The "Total" and "Details" columns use native <td rowSpan> to visually merge cells for
// clustered rows - only the cluster-owning row renders that cell at all; the browser spans
// it down over the follower rows beneath it.
import { GitMerge, Plus, RefreshCw, Split, Trash2, ArrowDown, ArrowUp } from 'lucide-react';
import React, { useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import CurrencyInput from './CurrencyInput';
import ImageUploadCell from './ImageUploadCell';
import {
  addItemAfter,
  applyDimensionChange,
  applyManualQtyChange,
  applyQtyChangeRecomputeTotal,
  applyRateChange,
  computeClusterSizes,
  computeClusterStarts,
  mergeDetailsUp as mergeDetailsUpAction,
  mergeTotalUp as mergeTotalUpAction,
  moveItem,
  removeItem,
  resetQtyToAuto,
  unmergeDetails,
  unmergeTotal,
} from './interiorQuotationModel';

const fieldClass =
  'h-10 w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-sm text-gray-800 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30';

const iconBtnClass =
  'inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 transition-colors hover:border-teal-300 hover:text-teal-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-200 disabled:hover:text-gray-500';

const RoomItemsTable = ({ items, onItemsChange, siteId = null }) => {
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState(null);
  const detStarts = computeClusterStarts(items, 'mergeDetailsUp');
  const detSizes = computeClusterSizes(detStarts);
  const totStarts = computeClusterStarts(items, 'mergeTotalUp');
  const totSizes = computeClusterSizes(totStarts);

  const updateItem = (index, updater) => {
    const next = [...items];
    next[index] = updater(next[index]);
    onItemsChange(next);
  };

  const pendingItem =
    pendingRemoveIndex != null ? items[pendingRemoveIndex] : null;

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100">
      <table className="min-w-full border-collapse text-sm">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="px-2 py-2 text-left font-semibold">#</th>
            <th className="min-w-[180px] px-2 py-2 text-left font-semibold">
              Unit Name <span className="text-red-500">*</span>
            </th>
            <th className="min-w-[90px] px-2 py-2 text-left font-semibold">Width (ft)</th>
            <th className="min-w-[90px] px-2 py-2 text-left font-semibold">Height (ft)</th>
            <th className="min-w-[90px] px-2 py-2 text-left font-semibold">Depth (ft)</th>
            <th className="min-w-[110px] px-2 py-2 text-left font-semibold">SFT</th>
            <th className="min-w-[120px] px-2 py-2 text-left font-semibold">Rate (₹)</th>
            <th className="min-w-[130px] px-2 py-2 text-left font-semibold">Total (₹)</th>
            <th className="min-w-[220px] px-2 py-2 text-left font-semibold">Details</th>
            <th className="min-w-[150px] px-2 py-2 text-right font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const isDetOwner = detStarts[index] === index;
            const isTotOwner = totStarts[index] === index;
            const showAutoBadge =
              !item.qtyIsManual && item.widthFt !== '' && item.heightFt !== '' && item.qty !== '';

            return (
              <tr key={item.id} className="border-t border-gray-100 align-top">
                <td className="px-2 py-2 text-gray-500">{index + 1}</td>
                <td className="px-2 py-2">
                  <input
                    type="text"
                    value={item.unitName}
                    onChange={(e) => updateItem(index, (it) => ({ ...it, unitName: e.target.value }))}
                    placeholder="e.g. Wardrobe - Sliding Shutter"
                    className={fieldClass}
                  />
                  <ImageUploadCell
                    image={item.image}
                    siteId={siteId}
                    onChange={(image) => updateItem(index, (it) => ({ ...it, image }))}
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.widthFt}
                    onChange={(e) =>
                      updateItem(index, (it) => applyDimensionChange(it, 'widthFt', e.target.value))
                    }
                    placeholder="ft"
                    className={fieldClass}
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.heightFt}
                    onChange={(e) =>
                      updateItem(index, (it) => applyDimensionChange(it, 'heightFt', e.target.value))
                    }
                    placeholder="ft"
                    className={fieldClass}
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.depthFt ?? ''}
                    onChange={(e) =>
                      updateItem(index, (it) => applyDimensionChange(it, 'depthFt', e.target.value))
                    }
                    placeholder="ft"
                    className={fieldClass}
                  />
                </td>
                <td className="px-2 py-2">
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.qty}
                      onChange={(e) =>
                        updateItem(index, (it) =>
                          applyQtyChangeRecomputeTotal(applyManualQtyChange(it, e.target.value))
                        )
                      }
                      placeholder="0"
                      className={`${fieldClass} ${showAutoBadge ? 'bg-teal-50/60 text-teal-800' : ''}`}
                      title={showAutoBadge ? 'Auto-computed from Width × Height' : undefined}
                    />
                    {item.qtyIsManual && item.widthFt !== '' && item.heightFt !== '' && (
                      <button
                        type="button"
                        onClick={() => updateItem(index, resetQtyToAuto)}
                        title="Reset to Width × Height"
                        className={iconBtnClass}
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-2 py-2">
                  <CurrencyInput
                    value={item.ratePerUnit ?? ''}
                    onChange={(val) => updateItem(index, (it) => applyRateChange(it, val))}
                    placeholder="₹"
                    className={fieldClass}
                  />
                </td>
                {isTotOwner && (
                  <td className="px-2 py-2" rowSpan={totSizes[index]}>
                    <CurrencyInput
                      value={item.unitTotal}
                      onChange={(val) => updateItem(index, (it) => ({ ...it, unitTotal: val }))}
                      placeholder="₹0"
                      className={`${fieldClass} font-semibold`}
                    />
                    {totSizes[index] > 1 && (
                      <p className="mt-1 text-[11px] text-gray-400">
                        Combined total for {totSizes[index]} rows
                      </p>
                    )}
                  </td>
                )}

                {isDetOwner && (
                  <td className="px-2 py-2" rowSpan={detSizes[index]}>
                    <textarea
                      value={item.details}
                      onChange={(e) => updateItem(index, (it) => ({ ...it, details: e.target.value }))}
                      placeholder="Material / hardware / finish spec..."
                      rows={Math.min(4, Math.max(2, detSizes[index]))}
                      className="w-full resize-y rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 text-sm text-gray-800 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                    />
                    {detSizes[index] > 1 && (
                      <p className="mt-1 text-[11px] text-gray-400">
                        Shared spec for {detSizes[index]} rows
                      </p>
                    )}
                  </td>
                )}

                <td className="px-2 py-2">
                  <div className="flex flex-wrap items-center justify-end gap-1">
                    {item.mergeTotalUp ? (
                      <button
                        type="button"
                        onClick={() => onItemsChange(unmergeTotal(items, index))}
                        title="Unmerge total from row above"
                        className={`${iconBtnClass} text-amber-600 hover:border-amber-300 hover:text-amber-700`}
                      >
                        <Split className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onItemsChange(mergeTotalUpAction(items, index))}
                        disabled={index === 0}
                        title="Merge total with row above"
                        className={`${iconBtnClass} text-amber-600 hover:border-amber-300 hover:text-amber-700`}
                      >
                        <GitMerge className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {item.mergeDetailsUp ? (
                      <button
                        type="button"
                        onClick={() => onItemsChange(unmergeDetails(items, index))}
                        title="Unmerge details from row above"
                        className={iconBtnClass}
                      >
                        <Split className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onItemsChange(mergeDetailsUpAction(items, index))}
                        disabled={index === 0}
                        title="Merge details with row above"
                        className={iconBtnClass}
                      >
                        <GitMerge className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onItemsChange(moveItem(items, index, -1))}
                      disabled={index === 0}
                      title="Move up"
                      className={iconBtnClass}
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onItemsChange(moveItem(items, index, 1))}
                      disabled={index === items.length - 1}
                      title="Move down"
                      className={iconBtnClass}
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onItemsChange(addItemAfter(items, index))}
                      title="Add row below"
                      className={iconBtnClass}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingRemoveIndex(index)}
                      disabled={items.length === 1}
                      title="Remove row"
                      className={`${iconBtnClass} text-red-500 hover:border-red-300 hover:text-red-700`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <ConfirmDialog
        isOpen={pendingRemoveIndex != null}
        title="Remove row?"
        message={`This will permanently remove "${
          pendingItem?.unitName || 'this row'
        }". This cannot be undone.`}
        confirmLabel="Remove"
        onCancel={() => setPendingRemoveIndex(null)}
        onConfirm={() => {
          const index = pendingRemoveIndex;
          setPendingRemoveIndex(null);
          if (index != null) onItemsChange(removeItem(items, index));
        }}
      />
    </div>
  );
};

export default RoomItemsTable;
